import React from "react";
import { motion, AnimatePresence } from "framer-motion";
import { PlayingCard } from "../PlayingCard";
import { t } from "../../lib/i18n";

function FloatingCard({ card, side, onClick, testId }) {
  const synthetic = card
    ? { rank: card.rank, suit: card.suit, is_joker: false, id: `preview-${card.rank}-${card.suit}` }
    : null;

  return (
    <motion.div
      role="button"
      tabIndex={0}
      data-testid={testId}
      onClick={onClick}
      onKeyDown={(e) => e.key === "Enter" && onClick()}
      whileHover={{ y: -12, scale: 1.07 }}
      whileTap={{ y: -4, scale: 0.95 }}
      transition={{ type: "spring", stiffness: 420, damping: 24 }}
      className="flex flex-col items-center gap-2 cursor-pointer select-none outline-none"
    >
      {/* Card — pointer-events-none so only the wrapper handles hover */}
      {synthetic && (
        <div
          className="pointer-events-none"
          style={{ filter: "drop-shadow(0 12px 28px rgba(0,0,0,0.7))" }}
        >
          <PlayingCard card={synthetic} size="md" disabled />
        </div>
      )}

      {/* Direction */}
      <span
        className="text-[10px] font-bold tracking-[0.14em] uppercase"
        style={{ color: "rgba(251,191,36,0.6)" }}
      >
        {side === "left" ? t("joker.left") : t("joker.right")}
      </span>
    </motion.div>
  );
}

export const JokerInsertDialog = ({ open, onCancel, onChoose, leftCard, rightCard }) => (
  <AnimatePresence>
    {open && (
      <>
        {/* Backdrop — dark, no blur */}
        <motion.div
          className="fixed inset-0 z-[200] bg-black/75"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.18 }}
          onClick={onCancel}
        />

        {/* Centered panel */}
        <div className="fixed inset-0 z-[201] flex items-center justify-center pointer-events-none">
          <motion.div
            className="pointer-events-auto"
            initial={{ scale: 0.86, opacity: 0, y: 6 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.86, opacity: 0, y: 6 }}
            transition={{ type: "spring", stiffness: 420, damping: 30 }}
            data-testid="joker-insert-dialog"
          >
            <div
              className="rounded-2xl"
              style={{
                background: "rgb(8, 10, 20)",
                border: "1px solid rgba(255,255,255,0.08)",
                boxShadow: [
                  "0 0 0 1px rgba(255,255,255,0.04)",
                  "0 30px 70px rgba(0,0,0,0.9)",
                ].join(", "),
              }}
            >
              <div className="px-5 pt-4 pb-4">
                {/* Compact title */}
                <p
                  className="text-center font-semibold uppercase mb-4"
                  style={{ fontSize: 11, letterSpacing: "0.12em", color: "rgba(148,163,184,0.45)" }}
                >
                  {t("joker.title")}
                </p>

                {/* Two floating cards */}
                {(leftCard || rightCard) ? (
                  <div className="flex gap-5 justify-center items-end">
                    {leftCard && (
                      <FloatingCard
                        card={leftCard}
                        side="left"
                        onClick={() => onChoose("left")}
                        testId="joker-insert-left-btn"
                      />
                    )}
                    {rightCard && (
                      <FloatingCard
                        card={rightCard}
                        side="right"
                        onClick={() => onChoose("right")}
                        testId="joker-insert-right-btn"
                      />
                    )}
                  </div>
                ) : (
                  // Fallback without card previews
                  <div className="flex gap-5 justify-center">
                    {[
                      { side: "left",  label: t("joker.left"),  testId: "joker-insert-left-btn"  },
                      { side: "right", label: t("joker.right"), testId: "joker-insert-right-btn" },
                    ].map(({ side, label, testId }) => (
                      <motion.button
                        key={side}
                        data-testid={testId}
                        onClick={() => onChoose(side)}
                        whileHover={{ y: -8, scale: 1.05 }}
                        whileTap={{ scale: 0.95 }}
                        transition={{ type: "spring", stiffness: 420, damping: 24 }}
                        className="flex flex-col items-center gap-2 cursor-pointer outline-none"
                      >
                        <div
                          className="w-14 h-20 rounded-lg flex items-center justify-center"
                          style={{
                            background: "rgba(255,255,255,0.04)",
                            border: "1px dashed rgba(251,191,36,0.3)",
                          }}
                        >
                          <span style={{ color: "rgba(251,191,36,0.5)", fontSize: 20 }}>★</span>
                        </div>
                        <span
                          className="font-bold uppercase"
                          style={{ fontSize: 10, letterSpacing: "0.14em", color: "rgba(251,191,36,0.6)" }}
                        >
                          {label}
                        </span>
                      </motion.button>
                    ))}
                  </div>
                )}

                {/* Cancel — pill button */}
                <div className="flex justify-center mt-5">
                  <motion.button
                    onClick={onCancel}
                    data-testid="joker-insert-cancel-btn"
                    whileHover={{ scale: 1.03 }}
                    whileTap={{ scale: 0.97 }}
                    transition={{ type: "spring", stiffness: 500, damping: 28 }}
                    className="px-6 py-[7px] rounded-full text-[12px] font-medium transition-colors duration-150 hover:text-slate-300"
                    style={{
                      color: "rgba(148,163,184,0.5)",
                      border: "1px solid rgba(255,255,255,0.1)",
                      letterSpacing: "0.02em",
                    }}
                  >
                    {t("common.cancel")}
                  </motion.button>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </>
    )}
  </AnimatePresence>
);

export default JokerInsertDialog;
